const SYNTHESIS_JSON_KEYS = ['task', 'compound_name', 'compound_smiles', 'reaction_order'] as const

export function isSynthesisPayload(obj: unknown): obj is Record<string, unknown> {
  if (!obj || typeof obj !== 'object') {
    return false
  }
  return SYNTHESIS_JSON_KEYS.every(key => key in obj)
}

function isJsonIdentChar(ch: string | undefined): boolean {
  return !!ch && /[\w$]/.test(ch)
}

/** 将 Python 风格 dict（单引号、True/False/None）转为标准 JSON 字符串 */
export function normalizePythonLikeJson(input: string): string {
  let out = ''
  let i = 0
  while (i < input.length) {
    const tail = input.slice(i)
    if (tail.startsWith('True') && !isJsonIdentChar(input[i - 1]) && !isJsonIdentChar(input[i + 4])) {
      out += 'true'
      i += 4
      continue
    }
    if (tail.startsWith('False') && !isJsonIdentChar(input[i - 1]) && !isJsonIdentChar(input[i + 5])) {
      out += 'false'
      i += 5
      continue
    }
    if (tail.startsWith('None') && !isJsonIdentChar(input[i - 1]) && !isJsonIdentChar(input[i + 4])) {
      out += 'null'
      i += 4
      continue
    }

    const char = input[i]
    if (char === '\'') {
      out += '"'
      i++
      while (i < input.length) {
        const c = input[i]
        if (c === '\\' && i + 1 < input.length) {
          const next = input[i + 1]
          if (next === '\'') {
            out += '\''
            i += 2
            continue
          }
          out += `\\${next}`
          i += 2
          continue
        }
        if (c === '\'') {
          out += '"'
          i++
          break
        }
        if (c === '"') {
          out += '\\"'
        }
        else if (c < ' ') {
          out += `\\u${c.charCodeAt(0).toString(16).padStart(4, '0')}`
        }
        else {
          out += c
        }
        i++
      }
      continue
    }

    out += char
    i++
  }
  return out
}

/** 提取首个完整 JSON / Python dict 对象 */
export function extractCompleteJson(str: string): { json: string, endIndex: number } | null {
  let depth = 0
  let inString: '\'' | '"' | null = null
  let escape = false
  let started = false

  for (let i = 0; i < str.length; i++) {
    const char = str[i]
    if (inString) {
      if (escape) {
        escape = false
      }
      else if (char === '\\') {
        escape = true
      }
      else if (char === inString) {
        inString = null
      }
      continue
    }
    if (char === '"' || char === '\'') {
      inString = char
      continue
    }
    if (char === '{') {
      depth++
      started = true
    }
    else if (char === '}') {
      depth--
      if (started && depth === 0) {
        return { json: str.slice(0, i + 1), endIndex: i + 1 }
      }
    }
  }
  return null
}

/** 解析标准 JSON 或 Python 风格 dict */
export function parseLooseJsonObject(raw: string): Record<string, unknown> | null {
  const trimmed = raw.trim()
  if (!trimmed.startsWith('{')) {
    return null
  }

  const candidates = [trimmed]
  if (trimmed.includes('\'')) {
    candidates.push(normalizePythonLikeJson(trimmed))
  }

  for (const candidate of candidates) {
    try {
      const obj = JSON.parse(candidate)
      if (obj && typeof obj === 'object') {
        return obj as Record<string, unknown>
      }
    }
    catch {
      /* try next */
    }
  }
  return null
}

/** 从流式缓冲中提取完整 JSON 对象，兼容 Python dict 与跨 chunk 拼接 */
export function extractStreamJsonObjects(input: string): { items: Record<string, unknown>[], rest: string } {
  const items: Record<string, unknown>[] = []
  let offset = 0

  while (offset < input.length) {
    while (offset < input.length && /\s/.test(input[offset])) {
      offset++
    }
    if (offset >= input.length) {
      break
    }

    const braceIndex = input.indexOf('{', offset)
    if (braceIndex === -1) {
      return { items, rest: input.slice(offset) }
    }

    if (braceIndex > offset) {
      offset = braceIndex
    }

    const extracted = extractCompleteJson(input.slice(offset))
    if (!extracted) {
      return { items, rest: input.slice(offset) }
    }

    const obj = parseLooseJsonObject(extracted.json)
    if (obj) {
      if (typeof obj.type === 'string') {
        items.push(obj)
      }
      else if (isSynthesisPayload(obj)) {
        items.push({ type: 'content', content: extracted.json })
      }
      else {
        items.push(obj)
      }
    }

    offset += extracted.endIndex
  }

  return { items, rest: '' }
}
